import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnmount } from 'react-use';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import HeartSVG from '../../../assets/imgs/heart-icon.svg?react';
import CheckSVG from '../../../assets/imgs/icon/checkmark.svg?react';

import {
	FavoriteProductPayload,
	useUpdateFavoritesInfoMutation
} from '../../../Account/api';
import { setFavoriteProducts } from '../../../Account/state/profileSlice';
import { transItemState } from '../../../atoms';
import { useAddCartItem } from '../../../Cart/hooks/useAddCartItem';
import { useDeleteTransItem } from '../../../Cart/hooks/api';
import { Flex } from '../../../GlobalComponents/Flex';
import { Button } from '../../../GlobalComponents/Button';
import { isCartModalOpenState } from '../../../Header/atoms';
import { RootStates } from '../../../reduxstore';
import { useAppDispatch, useAppSelector } from '../../../reduxstore/hooks';
import { isStoreGlobalState, storeState } from '../../../Store/atoms';
import { StoreFieldsFragment } from '../../../Store/graphql';
import { getStoreOpenStatus } from '../../../Store/utils/getStoreOpenStatus';
import { getNow } from '../../../utils/getNow';
import { useSelectProduct } from '../../hooks/api';
import { useClickDone } from '../../hooks/useClickDone';
import PickFavoriteItem from '../PickFavoriteItems';
import { setStoreSelection } from '../../../domains/UI/store/slice';
import { useWindowSize } from 'react-use';
// import {
// 	on_product_add_cart,
// 	on_product_add_favorite
// } from '../../../utils/gtag';
// import { validateProductEdit } from './validateProductEdit';

export type ProductFooterProps = {
	fixPosition?: boolean;
	isEdit?: boolean;
};

export const ProductFooter: React.FC<ProductFooterProps> = ({
	fixPosition,
	isEdit = false
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { width: screenWidth } = useWindowSize();

	const [showPickFavorites, setShowPickFavorites] = useState(false);
	const unavailableProductIds = useAppSelector(
		(state: RootStates) => state.uiReducer.unavailableProductIds
	);
	const selectedStore = useRecoilValue(storeState) as StoreFieldsFragment;
	const isStoreGlobal = useRecoilValue(isStoreGlobalState);
	const transItem = useRecoilValue(transItemState);

	const setIsCartModalOpen = useSetRecoilState(isCartModalOpenState);

	const favoriteProducts: Array<FavoriteProduct> = useAppSelector(
		state => state.accountReducer.profile.favorite_products
	);

	const userId = useAppSelector(state => state.authReducer.id);
	const productId = `${transItem?.item.productId}`;
	const isNotAvailable = unavailableProductIds.includes(Number(productId));
	const isFavorite = favoriteProducts
		? favoriteProducts.find(product => `${product.product}` === productId)
		: false;
	const isAuthenticated = useAppSelector(
		state => state.authReducer.accessToken
	);

	const [updateFavoritesInfo] = useUpdateFavoritesInfoMutation();

	const dispatch = useAppDispatch();
	const clickDone = useClickDone();
	const selectProduct = useSelectProduct();

	const navigateBack = () => navigate(-1);

	const addCartItem = useAddCartItem(navigateBack);

	// prompt location select upon product selection
	useEffect(() => {
		if (isStoreGlobal) {
			dispatch(
				setStoreSelection({
					isOpen: true,
					forSettingOLOStore: true,
					source: 'PRODUCT_FOOTER'
				})
			);
		}
	}, [dispatch, isStoreGlobal]);

	const clickHandler = async () => {
		const [valid] = await clickDone();
		if (!valid) return;

		const storeOpenStatus = getStoreOpenStatus(selectedStore, getNow());

		if (
			isStoreGlobal ||
			selectedStore?.oLOEnabled !== true ||
			storeOpenStatus !== 'OPEN'
		) {
			dispatch(
				setStoreSelection({
					isOpen: true,
					forSettingOLOStore: true,
					source: 'PRODUCT_FOOTER'
				})
			);
			return;
		}
		// if (!isEdit && transItem?.item.product.name)
		// on_product_add_cart({
		// 	product_cart_name: transItem?.item.product.name
		// });
		addCartItem();
		selectProduct(Number(productId));
		setIsCartModalOpen(true);
	};

	const favoriteHandler = async () => {
		if (isAuthenticated) {
			if (isFavorite) {
				const request = await updateFavoritesInfo({
					id: userId,
					favorite_products: favoriteProducts.filter(
						favoriteProduct =>
							`${favoriteProduct.product}` !== productId
					)
				});
				if (request) {
					return dispatch(
						setFavoriteProducts(
							(request as any).data.data.profile.favorite_products
						)
					);
				} else {
					// console.log('handle error here');
				}
			}

			if (favoriteProducts.length >= 5) {
				return setShowPickFavorites(true);
			}

			if (!isFavorite) {
				const payload = {
					id: userId,
					favorite_products: [
						...favoriteProducts,
						{ product: productId }
					]
				};
				const request = await updateFavoritesInfo(
					payload as FavoriteProductPayload
				);
				if (request) {
					dispatch(
						setFavoriteProducts(
							(request as any).data.data.profile.favorite_products
						)
					);
				} else {
				}
			}
		} else {
			navigate(`/login?redirect=${location.pathname}`, { replace: true });
		}
	};

	const deleteTransItem = useDeleteTransItem();

	const itemPrice = transItem?.price;

	useUnmount(() => {
		if (transItem && !transItem.isSent && !isEdit) {
			deleteTransItem(transItem.id);
		}
	});

	const editItem = async () => {
		const [valid] = await clickDone();
		if (!valid) return;
		// validateProductEdit(transItem);
		navigate('/checkout');
	};

	return (
		<>
			{showPickFavorites && favoriteProducts && (
				<PickFavoriteItem
					isOpen={showPickFavorites}
					toggleHandler={() =>
						setShowPickFavorites(!showPickFavorites)
					}
					favoriteProducts={favoriteProducts}
					newItem={{
						product: transItem?.item.product.id,
						product_name: transItem?.item.product.ticketName,
						priority: null
					}}
				/>
			)}
			<Flex
				$height="80px"
				$backgroundColor="#fff"
				style={{
					zIndex: 100,
					borderTop: '1px solid #f47920',
					position: fixPosition ? 'fixed' : 'relative',
					bottom: screenWidth > 992 ? '0px' : '90px'
				}}
				$padding={'0 1rem'}
				$justifyContent="center"
			>
				<Flex $width={'100%'} $maxWidth={'1140px'} $padding={'1rem 0'}>
					<Flex $alignItems="center" $justifyContent="space-between">
						<h2
							style={{
								color: '#000',
								paddingRight: '12px',
								fontSize: 'calc(1.325rem + .9vw)',
								marginBottom: '0',
								marginTop: 'auto',
								verticalAlign: 'bottom',
								fontWeight: '400'
							}}
						>
							${itemPrice}
						</h2>
						<Flex $columnGap={'12px'} $justifyContent="flex-end">
							{!isEdit && (
								<Button
									$minWidth={'80px'}
									$backgroundColor={'#dc3545'}
									disabled={isNotAvailable}
									onClick={favoriteHandler}
								>
									{screenWidth > 768 ? (
										'Add to Favorites'
									) : (
										<HeartSVG />
									)}
								</Button>
							)}

							<Button
								$minWidth={'80px'}
								disabled={isNotAvailable}
								onClick={isEdit ? editItem : clickHandler}
								$backgroundColor={
									isEdit ? '#30cad6' : '#f47920'
								}
							>
								{screenWidth > 768 ? (
									isEdit ? (
										'Checkout'
									) : (
										'Add to Cart'
									)
								) : (
									<CheckSVG />
								)}
							</Button>
						</Flex>
					</Flex>
				</Flex>
			</Flex>
		</>
	);
};
